<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import EnButton from 'Components/base/EnButton/EnButton.vue';
import EnEditableDataFieldsViewSection from 'Components/forms/EnEditableDataFieldsViewSection/EnEditableDataFieldsViewSection.vue';
import { enFetch } from 'Js/utils/fetchUtils';
import { EnButtonVariants } from 'Components/types/EnButtonVariants';

const {t} = useI18n();
const modelValue = defineModel('modelValue');
const emit = defineEmits(['input', 'update:modelValue', 'message-receive']);

const user = computed({
  get() {
    return modelValue.value;
  },
  set(value) {
    emit('input', value);
    emit('update:modelValue', value);
  },
});

const availableProviders = computed(() => {
  const others = (user.value && user.value.OtherLogins) ? user.value.OtherLogins : [];
  const currentNames = (user.value && user.value.CurrentLogins) ? user.value.CurrentLogins.map(c => c.LoginProvider) : [];
  return others.filter(p => !currentNames.includes(p.Name));
});

async function disconnectAd(loginProvider, providerKey) {
  const model = new URLSearchParams({
    loginProvider,
    providerKey,
  });
  const res = await enFetch({
    url: `/api/Configuration/RemoveExternalLogin?${model}`,
    method: 'post',
  });

  if (res.ok) {
    emit('message-receive', t('Authentication.manage.external.disconnectSuccess'), 'success');
    const loginValue = user.value.CurrentLogins.find(
        (login) => login.LoginProvider === loginProvider && login.ProviderKey === providerKey
    );
    user.value.OtherLogins.push({
      DisplayName: loginValue.ProviderDisplayName,
      Name: loginValue.LoginProvider,
    });
    user.value.CurrentLogins = user.value.CurrentLogins.filter(
        (login) => !(login.LoginProvider === loginProvider && login.ProviderKey === providerKey)
    );
  } else {
    emit('message-receive', await res.text(), 'error');
  }
}

async function connectAd(provider) {
  window.open(`/api/Configuration/LinkLoginWithAd?provider=${provider.Name}`, '_self');
}

function getInitials(name = '') {
  return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
}
</script>

<template>
  <en-editable-data-fields-view-section class="external-section"
                                        :fields="[{ key: 'External', custom: true, slotName: 'external' }]"
                                        :section-heading="t('Authentication.manage.external.title')" :editable="false"
                                        :actionButtonText="user.CurrentLogins && user.CurrentLogins.length && user.ShowRemoveButton ? t('Authentication.manage.external.disconnectButtonTextV2') : null"
                                        @action-button-click="() => disconnectAd(user.CurrentLogins[0].LoginProvider, user.CurrentLogins[0].ProviderKey)">
    <template #external>
      <div class="details-page-container">
        <template v-if="user.CurrentLogins && user.CurrentLogins.length > 0">
          <div v-for="login in user.CurrentLogins" :key="login.ProviderKey"
               class="connected-item d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <v-avatar color="primary" size="40">
                {{ getInitials(user.DisplayName || login.ProviderDisplayName) }}
              </v-avatar>
              <div class="ml-3">
                <div class="user-name">{{ user.DisplayName || login.ProviderDisplayName }}</div>
                <div class="user-email">{{ user.Email }}</div>
              </div>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="en-row flex flex-wrap flex-auto align-center">
            <div class="w-full p-3 basis-0 grow max-w-full">
              <div class="external-login-prompt">
                {{ t('Authentication.manage.external.addNewExternalLoginLabel') }}
              </div>
            </div>
            <div class="p-3 grow-0 shrink-0 basis-auto w-auto max-w-full" v-for="provider in availableProviders" :key="provider.Name">
              <en-button
                  height="36px"
                  width="100px"
                  :variant="EnButtonVariants.FILLED"
                  static-id="dialog-connect-button"
                  @click="() => connectAd(provider)"
              >
                {{ provider.DisplayName }}
              </en-button>
            </div>
          </div>
        </template>

      </div>
    </template>
  </en-editable-data-fields-view-section>
</template>

<style scoped lang="scss">
@use "Styles/helper/variables.scss";

.user-name {
  font-size: 1rem;
  line-height: 1.5;
}

.user-email {
  font-size: 0.875rem;
  line-height: 1.25;
  color: variables.$en-80;
}

.external-login-prompt {
  font-size: 1rem;
  line-height: 1.25;
  color: variables.$en-80;
}

.external-section :deep(.fields-view-container) {
  grid-template-columns: max-content 1fr !important;
  column-gap: 0 !important;
}

.external-section :deep(.fields-view-container .field-container .details-page-container .en-row) {
  width: 100%;
}

.external-section :deep(.fields-view-container .field-container .details-page-container .en-row > div:last-child) {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}
</style>